import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Drawer, Stack, Group, Text, Badge, Divider, Box,
  SimpleGrid, ActionIcon, Tooltip, Tabs, Anchor, ScrollArea,
  FileButton, Button, Paper, Center, Table,
  Modal, TextInput, Textarea, NumberInput,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import {
  IconEdit, IconRefresh, IconPaperclip, IconX,
  IconFileText, IconDownload, IconTrash, IconCheck,
  IconTrendingDown, IconTrendingUp, IconMinus, IconChecklist,
} from '@tabler/icons-react'
import {
  uploadLicenseAttachment, deleteLicenseAttachment,
  uploadRenewalAttachment, deleteRenewalAttachment,
  unassignLicense, getLicenses, updateRenewal, deleteRenewal,
  API_BASE_URL,
} from '../../api/client'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts'
import SmartDateInput from '../SmartDateInput'

const API_BASE = API_BASE_URL

const downloadFile = async (url: string, filename: string) => {
  try {
    const proxyUrl = `${API_BASE}/licenses/download?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(filename)}`
    const res = await fetch(proxyUrl, { headers: { Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` } })
    if (!res.ok) throw new Error()
    const blob = await res.blob()
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = filename; a.click()
    URL.revokeObjectURL(a.href)
  } catch { window.open(url, '_blank') }
}

const STATUS_COLOR: Record<string, string> = { ACTIVE: 'teal', EXPIRING: 'orange', EXPIRED: 'red', INACTIVE: 'gray' }
const TYPE_COLOR:   Record<string, string> = { SUBSCRIPTION: 'blue', PERPETUAL: 'violet', MA: 'orange', OEM: 'teal' }
const TYPE_ICON:    Record<string, string> = { SUBSCRIPTION: '🔄', PERPETUAL: '♾️', MA: '🔧', OEM: '📦' }

function Field({ label, value }: { label: string; value?: string | null | React.ReactNode }) {
  return (
    <Box>
      <Text size="xs" c="dimmed" fw={500}>{label}</Text>
      {typeof value === 'string' || !value ? <Text size="sm">{value || '—'}</Text> : value}
    </Box>
  )
}

interface Props {
  license: any | null
  opened: boolean
  onClose: () => void
  onEdit: (license: any) => void
  onRenew: (license: any) => void
  onDelete?: (license: any) => void
  onRefresh?: () => void
}

export default function LicenseQuickView({ license, opened, onClose, onEdit, onRenew, onDelete, onRefresh }: Props) {
  const { t } = useTranslation()
  const STATUS_LABEL: Record<string, string> = {
    ACTIVE: t('license.statusActive', 'ACTIVE'), EXPIRING: t('license.statusExpiring', 'EXPIRING'),
    EXPIRED: t('license.statusExpired', 'EXPIRED'), INACTIVE: t('common.inactive', 'INACTIVE'),
  }
  const TYPE_LABEL: Record<string, string> = {
    SUBSCRIPTION: t('license.typeSubscription', 'SUBSCRIPTION'), PERPETUAL: t('license.typePerpetual', 'PERPETUAL'),
    MA: t('license.typeMA', 'MA'), OEM: t('license.typeOEM', 'OEM'),
  }
  const [localLicense, setLocalLicense] = useState<any>(null)
  const current = localLicense ?? license
  const [editingRenewal, setEditingRenewal] = useState<any>(null)
  const [renewalForm, setRenewalForm] = useState<any>({})
  const [savingRenewal, setSavingRenewal] = useState(false)
  const [deletingRenewalId, setDeletingRenewalId] = useState<number | null>(null)
  if (!current) return null

  const toInputDate = (d?: string | null) => d ? new Date(d).toISOString().slice(0, 10) : ''

  const syncLicense = async () => {
    try {
      const res = await getLicenses()
      const found = (res.data as any[]).find((l: any) => l.id === current.id)
      if (found) { setLocalLicense(found); onRefresh?.() }
    } catch {}
  }


  const openEditRenewal = (r: any) => {
    setEditingRenewal(r)
    setRenewalForm({
      renewedFrom: toInputDate(r.renewedFrom),
      renewedTo: toInputDate(r.renewedTo),
      cost: r.cost ?? '',
      orderNumber: r.orderNumber ?? '',
      vendor: r.vendor ?? '',
      note: r.note ?? '',
    })
  }

  const saveRenewal = async () => {
    if (!editingRenewal) return
    // ── บังคับกรอกราคาเหมือนกับตอนสร้าง renewal ใหม่ (handleRenewSubmit ใน LicensePage.tsx) —
    // เดิมจุดนี้ไม่มี validation ฝั่ง frontend เลย ต่อให้ backend บังคับแล้วก็ตาม (DTO/service แก้ไป
    // ก่อนหน้านี้แล้ว) คนแก้ไขยังเจอแค่ toast "Update failed" ทั่วไปแทนข้อความชัดเจนว่าขาดอะไร ──
    if (renewalForm.cost === null || renewalForm.cost === undefined || renewalForm.cost === '') {
      notifications.show({ color: 'red', message: t('license.renewalCostRequired', 'Renewal cost is required') })
      return
    }
    setSavingRenewal(true)
    try {
      await updateRenewal(editingRenewal.id, renewalForm)
      setLocalLicense((prev: any) => {
        const base = prev ?? current
        return {
          ...base,
          renewals: base.renewals.map((rn: any) => rn.id === editingRenewal.id ? { ...rn, ...renewalForm } : rn),
        }
      })
      notifications.show({ color: 'teal', message: 'Renewal updated' })
      setEditingRenewal(null)
      await syncLicense()
    } catch (err: any) {
      // ── NestJS ValidationPipe ส่ง message กลับมาเป็น array ของ string เวลา validate ไม่ผ่าน
      // หลายข้อพร้อมกัน (เช่น field เดียวชน @IsNumber() และ @Min(0) พร้อมกัน) — ถ้าส่ง array นี้
      // ตรงๆ เข้า notification, React render แต่ละ item ต่อกันโดยไม่มีตัวคั่นเลย อ่านไม่ออกว่าคือ
      // คนละข้อความ ── join ด้วย ", " ให้อ่านง่ายก่อนแสดงผลเสมอ ──
      const rawMsg = err?.response?.data?.message
      const msg = Array.isArray(rawMsg) ? rawMsg.join(', ') : (rawMsg ?? 'Update failed')
      notifications.show({ color: 'red', message: msg })
    } finally { setSavingRenewal(false) }
  }

  const confirmDeleteRenewal = async (renewalId: number) => {
    setDeletingRenewalId(renewalId)
    try {
      await deleteRenewal(renewalId)
      setLocalLicense((prev: any) => {
        const base = prev ?? current
        return { ...base, renewals: base.renewals.filter((rn: any) => rn.id !== renewalId) }
      })
      notifications.show({ color: 'teal', message: 'Renewal deleted' })
      await syncLicense()
    } catch (err: any) {
      notifications.show({ color: 'red', message: err?.response?.data?.message ?? 'Delete failed' })
    } finally { setDeletingRenewalId(null) }
  }

  const fmtDate  = (d?: string | null) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'
  const fmtPrice = (p?: number | null) => p ? `฿${Number(p).toLocaleString()}` : '—'

  const annualCost = (() => {
    const cost = Number(current.cost ?? 0)
    switch (current.billingCycle) {
      case 'MONTHLY':    return cost * 12
      case 'QUARTERLY':  return cost * 4
      case 'MULTI_YEAR': return current.contractYears ? cost / current.contractYears : cost
      default:           return cost
    }
  })()

  const billingLabel = current.billingCycle === 'MONTHLY'   ? '📅 Monthly'
    : current.billingCycle === 'QUARTERLY' ? '📅 Quarterly'
    : current.billingCycle === 'MULTI_YEAR' ? `📅 ${current.contractYears}yr Deal`
    : '📅 Yearly'

  const daysLeft   = current.daysUntilExpiry
  const expiryColor = daysLeft === null ? 'dimmed' : daysLeft < 0 ? 'red' : daysLeft <= 30 ? 'orange' : 'teal'

  return (
    <Drawer
      opened={opened}
      onClose={() => { setLocalLicense(null); onClose() }}
      position="right" size={480}
      withCloseButton={false}
      styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column', height: '100%' } }}
    >
      {/* Header */}
      <Box p="md" style={{ background: '#0f172a', flexShrink: 0 }}>
        <Group justify="space-between" mb={6}>
          <Group gap={6}>
            <Badge color={STATUS_COLOR[current.status] ?? 'gray'} variant="filled" size="sm">{STATUS_LABEL[current.status] ?? current.status}</Badge>
            <Badge color={TYPE_COLOR[current.type] ?? 'gray'} variant="light" size="sm">
              {TYPE_ICON[current.type]} {TYPE_LABEL[current.type] ?? current.type}
            </Badge>
            {current.billingCycle && current.type !== 'PERPETUAL' && (
              <Badge size="xs" color="indigo" variant="dot">{billingLabel}</Badge>
            )}
          </Group>
          <ActionIcon variant="subtle" color="gray" onClick={() => { setLocalLicense(null); onClose() }} size="sm">
            <IconX size={15} />
          </ActionIcon>
        </Group>
        <Text fw={700} size="md" c="white" lineClamp={2}>{current.productName}</Text>
        {current.version && <Text size="xs" c="gray.4" mt={2}>v{current.version}</Text>}
        {current.vendor  && <Text size="xs" c="gray.4">{current.vendor}</Text>}
      </Box>

      {/* Action bar */}
      <Box px="md" py={8} style={{ borderBottom: '1px solid #e2e8f0', background: '#f8fafc', flexShrink: 0 }}>
        <Group gap={6}>
          <Tooltip label={t("common.edit","Edit")}><ActionIcon variant="light" color="gray" size="sm" onClick={() => { onClose(); onEdit(current) }}><IconEdit size={14} /></ActionIcon></Tooltip>
          {current.type !== 'PERPETUAL' && (
            <Tooltip label={t("license.renew","Renew")}><ActionIcon variant="light" color="teal" size="sm" onClick={() => { onClose(); onRenew(current) }}><IconRefresh size={14} /></ActionIcon></Tooltip>
          )}
          {onDelete && (
            <Tooltip label={t("license.deleteLicense","Delete license")}><ActionIcon variant="light" color="red" size="sm" onClick={() => { onClose(); onDelete(current) }}><IconTrash size={14} /></ActionIcon></Tooltip>
          )}
        </Group>
      </Box>

      {/* Tabs */}
      <Tabs defaultValue="info" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Tabs.List px="md" style={{ flexShrink: 0 }}>
          <Tabs.Tab value="info" fz="xs">{t("license.tabInfo","Info")}</Tabs.Tab>
          <Tabs.Tab value="assignments" fz="xs">{t("license.tabAssignments","Assignments")} ({current.assignments?.length ?? 0})</Tabs.Tab>
          <Tabs.Tab value="renewals" fz="xs">{t("license.tabRenewals","Renewals")} ({current.renewals?.length ?? 0})</Tabs.Tab>
          <Tabs.Tab value="attachments" fz="xs">
            <Group gap={4}><IconPaperclip size={12} />{t("license.tabAttachments","Attachments")} ({current.attachments?.length ?? 0})</Group>
          </Tabs.Tab>
        </Tabs.List>

        {/* ── Info ── */}
        <Tabs.Panel value="info" style={{ flex: 1, overflow: 'hidden' }}>
          <ScrollArea h="100%" p="md">
            <Stack gap="md">
              {current.type !== 'PERPETUAL' && (
                <Box p="sm" style={{ background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                  <Group justify="space-between">
                    <Box>
                      <Text size="xs" c="dimmed">{t('license.expiryDate', 'Expiry Date')}</Text>
                      <Text size="sm" fw={600}>{current.isOnward ? `🔄 ${t('license.onward', 'Onward')}` : fmtDate(current.expiryDate)}</Text>
                    </Box>
                    {!current.isOnward && (
                      <Box ta="right">
                        <Text size="xs" c="dimmed">{t('license.daysLeft', 'Days Left')}</Text>
                        <Text size="sm" fw={700} c={expiryColor}>
                          {daysLeft === null ? '∞' : daysLeft < 0 ? t('license.daysOverdue', '{{count}}d overdue', { count: Math.abs(daysLeft) }) : `${daysLeft}d`}
                        </Text>
                      </Box>
                    )}
                  </Group>
                </Box>
              )}
              <SimpleGrid cols={2} spacing="sm">
                <Field label={t("license.orderPoNumber2","Order / PO Number")} value={current.orderNumber} />
                <Field label={t("license.licenseKey","License Key")}        value={current.licenseKey} />
                <Field label={t("license.totalSeats","Total Seats")}         value={current.totalSeats > 0 ? `${current.usedSeats ?? 0}/${current.totalSeats}` : '∞'} />
                <Field label={t("common.department","Department")}          value={current.departmentCode} />
                <Field label={t("license.managedBy","Managed By")}          value={current.managedBy} />
                <Field label={t("license.notifyEmail","Notify Email")}        value={current.notifyEmail} />
              </SimpleGrid>
              <Divider label={t("license.cost","Cost")} labelPosition="left" />
              <SimpleGrid cols={2} spacing="sm">
                <Field label={`Cost (${current.currency ?? 'THB'})`} value={fmtPrice(current.cost)} />
                {current.type !== 'PERPETUAL' && <Field label={t("license.annualCostCalc","Annual Cost (calc.)")} value={`฿${annualCost.toLocaleString()}`} />}
                {current.type !== 'PERPETUAL' && current.billingCycle && <Field label={t("license.billingCycle","Billing Cycle")} value={billingLabel} />}
                <Field label={t("license.purchaseDate","Purchase Date")} value={fmtDate(current.purchaseDate)} />
                <Field label={t("license.startDate","Start Date")}    value={fmtDate(current.startDate)} />
              </SimpleGrid>
              {current.notes && (<><Divider /><Field label={t("common.notes","Notes")} value={current.notes} /></>)}
            </Stack>
          </ScrollArea>
        </Tabs.Panel>

        {/* ── Assignments ── */}
        <Tabs.Panel value="assignments" style={{ flex: 1, overflow: 'hidden' }}>
          <ScrollArea h="100%" p="md">
            {(current.assignments?.length ?? 0) === 0 ? (
              <Center p="xl"><Text size="sm" c="dimmed">{t("license.noAssignments","No assignments yet")}</Text></Center>
            ) : (
              <Table striped highlightOnHover>
                <Table.Thead style={{ background: '#f8fafc' }}>
                  <Table.Tr>
                    {['Target', 'Type', 'Name', 'Assigned By', 'Date', ''].map(h => (
                      <Table.Th key={h} style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase' }}>{h}</Table.Th>
                    ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {current.assignments.map((a: any) => (
                    <Table.Tr key={a.id}>
                      <Table.Td><Text size="sm" fw={500}>{a.targetId}</Text></Table.Td>
                      <Table.Td><Badge size="xs" variant="light" color={a.targetType === 'USER' ? 'blue' : 'teal'}>{a.targetType}</Badge></Table.Td>
                      <Table.Td><Text size="sm" c="dimmed">{a.targetName || '—'}</Text></Table.Td>
                      <Table.Td><Text size="xs" c="dimmed">{a.assignedBy}</Text></Table.Td>
                      <Table.Td><Text size="xs" c="dimmed">{new Date(a.assignedAt).toLocaleDateString('en-GB')}</Text></Table.Td>
                      <Table.Td>
                        <Tooltip label={t("license.removeAssignment","Remove assignment")}>
                          <ActionIcon size="sm" variant="subtle" color="red"
                            onClick={async () => {
                              try { await unassignLicense(a.id); await syncLicense() }
                              catch { notifications.show({ color: 'red', message: 'Failed' }) }
                            }}>
                            <IconX size={13} />
                          </ActionIcon>
                        </Tooltip>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </ScrollArea>
        </Tabs.Panel>

        {/* ── Renewals ── */}
        <Tabs.Panel value="renewals" style={{ flex: 1, overflow: 'hidden' }}>
          <ScrollArea h="100%" p="md">
            {!current.renewals?.length ? (
              <Text size="sm" c="dimmed" ta="center" py="xl">{t("license.noRenewals","No renewals yet")}</Text>
            ) : (
              <Stack gap={8}>
                {(() => {
                  // ── ตาราง/กราฟราคาย้อนหลังทุกช่วง — ไทม์ไลน์เดียวกันหมดแล้ว (isOriginal +
                  // renewal ทั้งหมดอยู่ใน current.renewals ตัวเดียว ไม่ต้องแยกเช็ค originalCost/
                  // purchaseDate จาก License อีกต่อไป) ทุก license มีรายการ isOriginal เสมอ (ทั้ง
                  // ตอนสร้างใหม่และ backfill ให้ของเก่าไว้แล้ว) เลยไม่ต้องมี fallback label อีกเลย ──
                  let renewalCounter = 0
                  const periods = [...current.renewals]
                    .filter((r: any) => r.cost != null)
                    .sort((a: any, b: any) => new Date(a.renewedFrom).getTime() - new Date(b.renewedFrom).getTime())
                    .map((r: any) => {
                      if (r.isOriginal) return { period: t('license.original', 'Original'), date: r.renewedFrom, cost: Number(r.cost) }
                      renewalCounter++
                      return { period: t('license.renewalN', 'Renewal {{n}}', { n: renewalCounter }), date: r.renewedFrom, cost: Number(r.cost) }
                    })
                  if (periods.length < 2) return null

                  const first = periods[0].cost
                  const last = periods[periods.length - 1].cost
                  const sinceOriginalPct = first > 0 ? ((last - first) / first) * 100 : 0
                  const trendColor = sinceOriginalPct < 0 ? 'teal' : sinceOriginalPct > 0 ? 'red' : 'gray'
                  const TrendIcon = sinceOriginalPct < 0 ? IconTrendingDown : sinceOriginalPct > 0 ? IconTrendingUp : IconMinus

                  return (
                    <Paper withBorder radius="lg" p="md" mb={4}>
                      <Group justify="space-between" mb="sm">
                        <Group gap={6}>
                          <IconChecklist size={16} color="#0d9488" />
                          <Text size="sm" fw={700}>{t('license.priceHistory', 'Price History')}</Text>
                        </Group>
                        <Badge color={trendColor} variant="light" size="sm" leftSection={<TrendIcon size={12} />}>
                          {sinceOriginalPct > 0 ? '+' : ''}{sinceOriginalPct.toFixed(1)}% {t('license.sinceOriginal', 'SINCE ORIGINAL')}
                        </Badge>
                      </Group>

                      <Box h={160}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={periods} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} width={50}
                              tickFormatter={(v) => v >= 1000 ? `฿${(v/1000).toFixed(0)}k` : `฿${v}`} />
                            <RechartsTooltip formatter={(v: any) => [`฿${Number(v).toLocaleString()}`, t('license.cost', 'Cost')]} />
                            <Line type="monotone" dataKey="cost" stroke="#0d9488" strokeWidth={2} dot={{ r: 4 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>

                      <Divider my="sm" />

                      <Table verticalSpacing={4} fz="xs">
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th style={{ color: '#94a3b8', fontWeight: 500, fontSize: 10, textTransform: 'uppercase' }}>{t('license.period', 'Period')}</Table.Th>
                            <Table.Th style={{ color: '#94a3b8', fontWeight: 500, fontSize: 10, textTransform: 'uppercase' }}>{t('common.date', 'Date')}</Table.Th>
                            <Table.Th style={{ color: '#94a3b8', fontWeight: 500, fontSize: 10, textTransform: 'uppercase' }}>{t('license.cost', 'Cost')}</Table.Th>
                            <Table.Th style={{ color: '#94a3b8', fontWeight: 500, fontSize: 10, textTransform: 'uppercase' }}>{t('license.change', 'Change')}</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {periods.map((p, i) => {
                            const prev = i > 0 ? periods[i - 1].cost : null
                            const pct = prev !== null && prev > 0 ? ((p.cost - prev) / prev) * 100 : null
                            return (
                              <Table.Tr key={i}>
                                <Table.Td>{p.period}</Table.Td>
                                <Table.Td c="dimmed">{new Date(p.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</Table.Td>
                                <Table.Td fw={700}>฿{p.cost.toLocaleString()}</Table.Td>
                                <Table.Td>
                                  {pct === null ? '—' : (
                                    <Text size="xs" c={pct < 0 ? 'teal' : pct > 0 ? 'red' : 'dimmed'} fw={600}>
                                      {pct > 0 ? '+' : ''}{pct.toFixed(1)}%
                                    </Text>
                                  )}
                                </Table.Td>
                              </Table.Tr>
                            )
                          })}
                        </Table.Tbody>
                      </Table>
                    </Paper>
                  )
                })()}
                {(() => {
                  // ── เรียงตามเวลาจริงก่อนเสมอ (เก่า→ใหม่ ตาม renewedFrom) แล้วค่อย reverse เพื่อ
                  // แสดงผล — ของเดิม reverse() ตรงๆ จาก array ที่ได้จาก API ซึ่งเรียงตามลำดับที่ถูก
                  // สร้าง/บันทึกไว้ในฐานข้อมูล ไม่ใช่ลำดับเวลาจริงเสมอไป (เช่น แก้ไขวันที่ของ Original
                  // ย้อนหลังทีหลัง แต่ลำดับแถวใน DB ไม่เปลี่ยนตาม) ทำให้ตำแหน่งใน list สลับกันมั่วได้ —
                  // Original ต้องอยู่ล่างสุดเสมอ (เก่าสุด) แล้วไล่ขึ้นไปตามลำดับเวลาที่ใหม่ขึ้น ──
                  const sortedByDate = [...current.renewals].sort(
                    (a: any, b: any) => new Date(a.renewedFrom).getTime() - new Date(b.renewedFrom).getTime()
                  )
                  return [...sortedByDate].reverse().map((r: any, i: number) => {
                  // ── นับเลข Renewal ไม่รวมรายการ isOriginal (แสดงเป็น "ORIGINAL" แยกต่างหาก ไม่ใช่
                  // "Renewal #N" ปนกัน) — list กลับด้าน (ใหม่ก่อน) ต้องนับจากท้ายไทม์ไลน์จริง ──
                  const renewalIndexFromStart = sortedByDate.length - 1 - i // ตำแหน่งจริงในไทม์ไลน์ (เรียงเก่า→ใหม่)
                  const renewalNumber = sortedByDate
                    .slice(0, renewalIndexFromStart + 1)
                    .filter((x: any) => !x.isOriginal).length
                  return (
                  <Box key={i} p="sm" style={{ background: r.isOriginal ? '#fffbeb' : '#f8fafc', borderRadius: 8, border: `1px solid ${r.isOriginal ? '#fde68a' : '#e2e8f0'}` }}>
                    <Group justify="space-between" mb={4}>
                      <Badge size="xs" color={r.isOriginal ? 'yellow' : 'teal'} variant="light">
                        {r.isOriginal ? `📅 ${t('license.original', 'Original')}` : `Renewal #${renewalNumber}`}
                      </Badge>
                      <Group gap={4}>
                        <Text size="xs" c="dimmed">{fmtDate(r.createdAt)}</Text>
                        <Tooltip label={t("license.editRenewalTooltip","Edit renewal")}>
                          <ActionIcon size="xs" variant="subtle" color="gray" onClick={() => openEditRenewal(r)}>
                            <IconEdit size={12} />
                          </ActionIcon>
                        </Tooltip>
                        {/* ── ห้ามลบรายการ Original — ทุก license ต้องมีจุดเริ่มต้นในไทม์ไลน์เสมอ
                        (ตรงกับที่ backend บล็อกไว้ใน deleteRenewal()) แก้ไขได้ปกติ แค่ลบไม่ได้ ── */}
                        {!r.isOriginal && (
                          <Tooltip label={t("license.deleteRenewal","Delete renewal")}>
                            <ActionIcon
                              size="xs" variant="subtle" color="red"
                              loading={deletingRenewalId === r.id}
                              onClick={() => {
                                if (window.confirm(`ลบ Renewal #${renewalNumber} ใช่หรือไม่?`)) {
                                  confirmDeleteRenewal(r.id)
                                }
                              }}
                            >
                              <IconTrash size={12} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Group>
                    </Group>
                    <Text size="xs">{fmtDate(r.renewedFrom)} → {fmtDate(r.renewedTo)}</Text>
                    {r.cost       && <Text size="xs" c="teal.7" fw={500}>฿{Number(r.cost).toLocaleString()}</Text>}
                    {r.orderNumber && <Text size="xs" c="dimmed">PO: {r.orderNumber}</Text>}
                    {r.note       && <Text size="xs" c="dimmed" fs="italic">{r.note}</Text>}
                    {/* Renewal attachments */}
                    <Group gap={4} mt={6} wrap="wrap">
                      {(r.attachments ?? []).map((att: any, ai: number) => (
                        <Group key={ai} gap={2} wrap="nowrap"
                          style={{ background: '#fff', borderRadius: 6, padding: '2px 6px', border: '1px solid #e2e8f0' }}>
                          <Tooltip label={att.name}>
                            <ActionIcon size="xs" variant="subtle" color="blue" onClick={() => downloadFile(att.url, att.name)}>
                              <IconFileText size={12} />
                            </ActionIcon>
                          </Tooltip>
                          <Text size="xs" c="dimmed" style={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.name}</Text>
                          <ActionIcon size="xs" variant="subtle" color="red"
                            onClick={async () => {
                              try {
                                const res = await deleteRenewalAttachment(r.id, att.url)
                                setLocalLicense((prev: any) => ({ ...prev,
                                  renewals: prev.renewals.map((rn: any) => rn.id === r.id ? { ...rn, attachments: res.data.attachments } : rn)
                                }))
                              } catch { notifications.show({ color: 'red', message: 'Delete failed' }) }
                            }}>
                            <IconX size={10} />
                          </ActionIcon>
                        </Group>
                      ))}
                      <FileButton onChange={async (file) => {
                        if (!file) return
                        try {
                          const res = await uploadRenewalAttachment(r.id, file)
                          setLocalLicense((prev: any) => ({ ...prev,
                            renewals: prev.renewals.map((rn: any) => rn.id === r.id ? { ...rn, attachments: res.data.attachments } : rn)
                          }))
                          notifications.show({ color: 'teal', message: 'File attached' })
                        } catch { notifications.show({ color: 'red', message: 'Upload failed' }) }
                      }} accept="*/*">
                        {(props) => (
                          <Tooltip label={t("license.attachFile","Attach file")}>
                            <ActionIcon {...props} size="xs" variant="subtle" color="teal"><IconPaperclip size={13} /></ActionIcon>
                          </Tooltip>
                        )}
                      </FileButton>
                    </Group>
                  </Box>
                  )
                  })
                })()}
              </Stack>
            )}
          </ScrollArea>
        </Tabs.Panel>

        {/* ── Attachments ── */}
        <Tabs.Panel value="attachments" style={{ flex: 1, overflow: 'hidden' }}>
          <ScrollArea h="100%" p="md">
            <Group justify="space-between" mb="sm">
              <Text size="sm" fw={600}>{t("license.filesAndDocuments","Files & Documents")}</Text>
              <FileButton onChange={async (file) => {
                if (!file) return
                try {
                  const res = await uploadLicenseAttachment(current.id, file)
                  setLocalLicense((prev: any) => ({ ...(prev ?? current), attachments: res.data.attachments }))
                  notifications.show({ color: 'teal', message: 'File uploaded' })
                } catch { notifications.show({ color: 'red', message: 'Upload failed' }) }
              }} accept="*/*">
                {(props) => (
                  <Button {...props} size="xs" variant="light" color="teal" leftSection={<IconPaperclip size={13} />}>
                    Upload File
                  </Button>
                )}
              </FileButton>
            </Group>

            {(current.attachments ?? []).length === 0 ? (
              <Center p="xl">
                <Stack align="center" gap={6}>
                  <IconPaperclip size={32} color="#cbd5e1" />
                  <Text size="sm" c="dimmed" ta="center">{t("license.noAttachments","No attachments yet")}</Text>
                </Stack>
              </Center>
            ) : (
              <Stack gap={8}>
                {(current.attachments ?? []).map((att: any, i: number) => (
                  <Paper key={i} withBorder p="sm" radius="md">
                    <Group justify="space-between">
                      <Group gap={8}>
                        <IconFileText size={18} color="#6366f1" />
                        <Box>
                          <Text size="sm" fw={500}>{att.name}</Text>
                          <Text size="xs" c="dimmed">{fmtDate(att.uploadedAt)}</Text>
                        </Box>
                      </Group>
                      <Group gap={4}>
                        <Tooltip label={t("common.download","Download")}>
                          <ActionIcon size="sm" variant="subtle" color="blue" onClick={() => downloadFile(att.url, att.name)}>
                            <IconDownload size={14} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label={t("common.delete","Delete")}>
                          <ActionIcon size="sm" variant="subtle" color="red"
                            onClick={async () => {
                              try {
                                const res = await deleteLicenseAttachment(current.id, att.url)
                                setLocalLicense((prev: any) => ({ ...(prev ?? current), attachments: res.data.attachments }))
                                notifications.show({ color: 'teal', message: 'File deleted' })
                              } catch { notifications.show({ color: 'red', message: 'Delete failed' }) }
                            }}>
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Group>
                  </Paper>
                ))}
              </Stack>
            )}
          </ScrollArea>
        </Tabs.Panel>
      </Tabs>

      {/* Edit Renewal Modal */}
      <Modal
        opened={!!editingRenewal}
        onClose={() => setEditingRenewal(null)}
        title={<Text fw={600}>{t("license.editRenewal","Edit Renewal")}</Text>}
        size="sm" centered
      >
        <Stack gap="sm">
          <Group grow>
            <SmartDateInput
              label={t("common.from","From")} placeholder={t("license.datePlaceholder","DD/MM/YYYY")}
              value={renewalForm.renewedFrom ?? ''}
              onChange={v => setRenewalForm((f: any) => ({ ...f, renewedFrom: v }))}
            />
            <SmartDateInput
              label={t("common.to","To")} placeholder={t("license.datePlaceholder","DD/MM/YYYY")}
              value={renewalForm.renewedTo ?? ''}
              onChange={v => setRenewalForm((f: any) => ({ ...f, renewedTo: v }))}
            />
          </Group>
          <NumberInput
            label={t("license.renewalCostLabel", "Renewal cost (฿) *")}
            placeholder={t("license.renewalCostPlaceholder", "Enter this renewal's actual price")}
            value={renewalForm.cost ?? ''}
            onChange={v => setRenewalForm((f: any) => ({ ...f, cost: v }))}
            thousandSeparator=","
            decimalScale={2}
            min={0}
          />
          <TextInput
            label={t("license.poOrderNumber","PO / Order Number")}
            value={renewalForm.orderNumber ?? ''}
            onChange={e => setRenewalForm((f: any) => ({ ...f, orderNumber: e.target.value }))}
          />
          <TextInput
            label={t("common.vendor","Vendor")}
            value={renewalForm.vendor ?? ''}
            onChange={e => setRenewalForm((f: any) => ({ ...f, vendor: e.target.value }))}
          />
          <Textarea
            label={t("common.note","Note")}
            value={renewalForm.note ?? ''}
            onChange={e => setRenewalForm((f: any) => ({ ...f, note: e.target.value }))}
            minRows={2}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => setEditingRenewal(null)} disabled={savingRenewal}>{t("common.cancel","Cancel")}</Button>
            <Button color="teal" leftSection={<IconCheck size={15} />} loading={savingRenewal} onClick={saveRenewal}>
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Drawer>
  )
}
